import { type FormEvent } from "react";
import { Button } from "../components/Button";
import { ChevronDownIcon, ShuffleIcon } from "../components/Icons";
import { ScreenShell } from "../components/ScreenShell";
import { useUiText } from "../i18n/useUiText";
import { AVAILABLE_THEMES } from "../session/themes";
import type { PlayerCount } from "../session/types";
import { useAppStore } from "../state/useAppStore";

const PLAYER_COUNTS: readonly PlayerCount[] = [3, 4, 5];

export function CreateScreen() {
  const t = useUiText();
  const draft = useAppStore((state) => state.createDraft);
  const isCreating = useAppStore((state) => state.isCreating);
  const sessionError = useAppStore((state) => state.sessionError);
  const goHome = useAppStore((state) => state.goHome);
  const setPlayerCount = useAppStore((state) => state.setPlayerCount);
  const setThemeIndex = useAppStore((state) => state.setThemeIndex);
  const randomizeTheme = useAppStore((state) => state.randomizeTheme);
  const startSession = useAppStore((state) => state.startSession);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void startSession();
  };

  const hasChoice = AVAILABLE_THEMES.length > 1;

  return (
    <ScreenShell
      backLabel={t("common.home")}
      footer={
        <Button
          disabled={isCreating}
          onClick={() => void startSession()}
          wide
        >
          {isCreating ? t("create.creating") : t("create.submit")}
        </Button>
      }
      onBack={goHome}
    >
      <div className="narrow-column">
        <header className="screen-heading">
          <h1>{t("create.title")}</h1>
        </header>

        <form className="setup-form" onSubmit={handleSubmit}>
          <fieldset className="choice-fieldset">
            <legend>{t("create.playerCount")}</legend>
            <div className="segmented-control segmented-control--three">
              {PLAYER_COUNTS.map((count) => (
                <label key={count}>
                  <input
                    checked={draft.playerCount === count}
                    name="player-count"
                    onChange={() => setPlayerCount(count)}
                    type="radio"
                    value={count}
                  />
                  <span>{count}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="choice-fieldset">
            <legend>{t("create.theme")}</legend>
            <div className="theme-picker">
              <div className="select-field">
                <select
                  aria-label={t("create.theme")}
                  onChange={(event) =>
                    setThemeIndex(Number(event.target.value))
                  }
                  value={draft.themeIndex}
                >
                  {AVAILABLE_THEMES.map((theme) => (
                    <option key={theme.key} value={theme.index}>
                      {theme.title}
                    </option>
                  ))}
                </select>
                <span className="select-field__icon" aria-hidden="true">
                  <ChevronDownIcon />
                </span>
              </div>
              {hasChoice ? (
                <Button
                  aria-label={t("create.randomThemeLabel")}
                  className="theme-picker__random"
                  icon={<ShuffleIcon />}
                  onClick={randomizeTheme}
                  variant="secondary"
                >
                  {t("create.randomTheme")}
                </Button>
              ) : null}
            </div>
          </fieldset>

          {sessionError ? (
            <p className="inline-error" role="alert">
              {t("error.session")}
            </p>
          ) : null}
        </form>
      </div>
    </ScreenShell>
  );
}
